import type { ReactNode } from 'react';
import { cn } from '@ai-tutor/ui';

/**
 * The classic view's timer: one quiet ring that empties as the phase runs,
 * with the time inside it. Rest (a break, a finished session) is green, so a
 * glance tells it from focus. Decorative: the time in `children` is what is
 * read out.
 */
export type RingTone = 'focus' | 'paused' | 'rest' | 'flow';

const ARC: Record<RingTone, string> = {
  focus: 'stroke-brand-500 dark:stroke-brand-300',
  paused: 'stroke-muted-foreground/60',
  rest: 'stroke-success/70',
  flow: 'stroke-brand-500/25 dark:stroke-brand-300/25',
};
const TRACK: Record<RingTone, string> = {
  focus: 'stroke-brand-200/70 dark:stroke-brand-500/20',
  paused: 'stroke-border',
  rest: 'stroke-success/15',
  flow: 'stroke-brand-200/70 dark:stroke-brand-500/20',
};

const R = 54;
const LENGTH = 2 * Math.PI * R;

interface TimerRingProps {
  /** 0-1 of the ring still drawn: the share of the phase left. */
  fraction: number;
  tone?: RingTone;
  className?: string;
  children: ReactNode;
}

export function TimerRing({
  fraction,
  tone = 'focus',
  className,
  children,
}: TimerRingProps) {
  const left = tone === 'flow' ? 1 : Math.min(1, Math.max(0, fraction));
  return (
    <div className={cn('relative shrink-0', className)}>
      <svg
        viewBox='0 0 120 120'
        className='h-full w-full -rotate-90'
        aria-hidden='true'
      >
        <circle
          cx='60'
          cy='60'
          r={R}
          fill='none'
          strokeWidth='2.6'
          className={TRACK[tone]}
        />
        <circle
          cx='60'
          cy='60'
          r={R}
          fill='none'
          strokeWidth='2.6'
          strokeLinecap='round'
          strokeDasharray={LENGTH}
          strokeDashoffset={LENGTH * (1 - left)}
          className={cn(
            'transition-[stroke-dashoffset,stroke] duration-slow',
            ARC[tone]
          )}
        />
      </svg>
      <div className='absolute inset-0 flex flex-col items-center justify-center gap-1 text-center'>
        {children}
      </div>
    </div>
  );
}
